import { useEffect, useState } from 'react';
import { api } from '../api/client';

export type PositionLockReason = 'RANKED' | 'OTHER_APPLICATION' | 'DISCONTINUED';

// Shown for a position the admin has closed (Applications Open unchecked in Admin → Positions).
export const POSITION_DISCONTINUED_MESSAGE =
  'This position is not accepting applications right now. An application for it that was never submitted cannot be continued.';

export interface OpenPositions {
  loading: boolean;
  /** roleIds open to applicants right now; null while loading or if the lookup failed. */
  openRoleIds: Set<string> | null;
  /** Open roleIds the admin hid from the public jobs page (their own page still works). */
  hiddenRoleIds: Set<string>;
  error: boolean;
}

/**
 * Which positions are open, straight from the server (GET /api/positions), which reads the admin's
 * Applications Open switch. Nothing on the client keeps its own list of open or closed positions.
 */
export function useOpenPositions(): OpenPositions {
  const [state, setState] = useState<OpenPositions>({ loading: true, openRoleIds: null, hiddenRoleIds: new Set(), error: false });
  useEffect(() => {
    let active = true;
    api.get<{ positions: Array<{ roleId: string; hiddenFromPublic?: boolean }> }>('/api/positions')
      .then((result) => {
        if (active) setState({
          loading: false,
          openRoleIds: new Set(result.positions.map((position) => position.roleId)),
          hiddenRoleIds: new Set(result.positions.filter((position) => position.hiddenFromPublic).map((position) => position.roleId)),
          error: false,
        });
      })
      .catch(() => { if (active) setState({ loading: false, openRoleIds: null, hiddenRoleIds: new Set(), error: true }); });
    return () => { active = false; };
  }, []);
  return state;
}

export interface PositionAvailability {
  roleId: string;
  started: boolean;
  status: string;
  locked: boolean;
  lockReason: PositionLockReason | null;
}

export interface ApplicationAvailability {
  ranked: boolean;
  activeRoleId: string;
  startedRoleId: string | null;
  positions: PositionAvailability[];
}

export const POSITION_LOCK_MESSAGES: Record<PositionLockReason, string> = {
  RANKED: 'You are already ranked with Krovance, so you cannot start another application.',
  OTHER_APPLICATION: 'You already have an application in progress for another position. Only one application can be open at a time.',
  DISCONTINUED: POSITION_DISCONTINUED_MESSAGE,
};

export function lockMessageFor(position: PositionAvailability | null): string | null {
  return position?.lockReason ? POSITION_LOCK_MESSAGES[position.lockReason] : null;
}

export function availabilityFor(availability: ApplicationAvailability | null, roleId: string): PositionAvailability | null {
  return availability?.positions.find((position) => position.roleId === roleId) ?? null;
}

/**
 * Why this position cannot be applied to, if it cannot. A closed position is checked first and
 * without the per-applicant lookup, so it reads as closed to everyone - signed out or signed in.
 */
export function positionLockMessage(roleId: string, availability: ApplicationAvailability | null, openRoleIds: Set<string> | null): string | null {
  if (openRoleIds && !openRoleIds.has(roleId)) return POSITION_DISCONTINUED_MESSAGE;
  return lockMessageFor(availabilityFor(availability, roleId));
}

/**
 * Loads which positions the signed-in applicant may still apply to. Only
 * meaningful for signed-in applicants, so `enabled` is false for signed-out
 * visitors and admins - they see every position as normal.
 */
export function useApplicationAvailability(enabled: boolean) {
  const [availability, setAvailability] = useState<ApplicationAvailability | null>(null);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      setAvailability(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    api.get<ApplicationAvailability>('/api/progress/positions')
      // A failed lookup must never hide the apply button - the POST that starts
      // an application enforces the same rule server-side regardless.
      .then((result) => { if (active) setAvailability(result); })
      .catch(() => { if (active) setAvailability(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [enabled]);

  return { availability, loading };
}
